import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { ROLE_LABEL, useAuth } from '../auth';
import { useApi } from './ui';
import { fmtDate } from '../api';
import { GlobalSearch } from './GlobalSearch';

// newWindow: painéis de TV abrem em outra janela/aba, fora da aplicação.
const NAV: { to: string; label: string; perm?: string; newWindow?: boolean }[] = [
  { to: '/', label: 'Command Center' },
  { to: '/assistente', label: 'Assistente (Claude)', perm: 'analytics:read' },
  { to: '/cockpit', label: 'CMO Cockpit' },
  { to: '/demandas', label: 'Demandas' },
  { to: '/aprovacoes', label: 'Aprovações', perm: 'approval:decide' },
  { to: '/calendario', label: 'Calendário Editorial' },
  { to: '/studio', label: 'Studio Criativo', perm: 'request:read_all' },
  { to: '/agentes', label: 'Agentes' },
  { to: '/agentes-3d', label: 'Agentes 3D (ao vivo)' },
  { to: '/radar', label: 'Radar de Mercado' },
  { to: '/portfolio', label: 'Portfólio & Distribuidores' },
  { to: '/campanhas', label: 'Campanhas' },
  { to: '/eventos', label: 'Eventos' },
  { to: '/endomarketing', label: 'Endomarketing AGIR' },
  { to: '/abm', label: 'ABM & Leads' },
  { to: '/prospeccao', label: 'Prospecção LinkedIn (Ravi)', perm: 'prospect:manage' },
  { to: '/atendimento-site', label: 'Atendimento do site (Dom)', perm: 'prospect:manage' },
  { to: '/analytics', label: 'Analytics / BI', perm: 'analytics:read' },
  { to: '/admin', label: 'Admin / Segurança' },
  { to: '/painel', label: 'Painel de TV ↗', perm: 'analytics:read', newWindow: true },
  { to: '/painel-3d', label: 'TV — Agentes 3D ↗', perm: 'analytics:read', newWindow: true },
];

export function Layout() {
  const { me, can, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ks = useApi<{ active?: boolean; reason?: string }>('/api/admin/killswitch');
  const notes = useApi<{ id: number; title: string; body: string | null; createdAt: string; readAt: string | null }[]>('/api/notifications');
  const [showNotes, setShowNotes] = useState(false);
  const unread = notes.data?.filter((n) => !n.readAt).length ?? 0;

  return (
    <div className="min-h-screen">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:p-2">
        Pular para o conteúdo
      </a>
      {me?.env === 'dev' && (
        <div className="bg-violet-800 px-4 py-1 text-center text-xs font-medium text-white">
          AMBIENTE DE DESENVOLVIMENTO — publicação {me.publishMode === 'assisted' ? 'ASSISTIDA (você publica e registra a URL)' : 'SIMULADA'} · LLM: {me.llm === 'mock' ? 'provedor simulado (mock)' : 'Claude (Anthropic)'} · Logo oficial aplicado; manual de marca pendente
        </div>
      )}
      {ks.data?.active && (
        <div role="alert" className="bg-red-700 px-4 py-2 text-center text-sm font-semibold text-white">
          KILL SWITCH ATIVO — todos os envios externos estão pausados. Motivo: {ks.data.reason}
        </div>
      )}
      <div className="flex">
        <aside className={`${open ? 'block' : 'hidden'} fixed inset-y-0 z-30 w-64 overflow-y-auto bg-brand-950 text-brand-100 lg:static lg:block`} aria-label="Navegação principal">
          <div className="border-b border-brand-800 px-4 py-4">
            <img src="/brand/3s-logo-negativo.svg" alt="3S Networks" className="h-12 w-auto" />
            <div className="mt-2 border-t-2 border-accent-400 pt-2 text-sm font-semibold text-white">AI Marketing Command Center</div>
          </div>
          <nav className="p-2">
            <ul className="space-y-0.5">
              {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
                <li key={n.to}>
                  {n.newWindow ? (
                    <a href={n.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="block rounded px-3 py-2 text-sm text-brand-100 hover:bg-brand-900" title="Abre em outra janela">
                      {n.label}
                    </a>
                  ) : (
                    <NavLink
                      to={n.to}
                      end={n.to === '/'}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => `block rounded px-3 py-2 text-sm ${isActive ? 'bg-brand-700 font-semibold text-white' : 'text-brand-100 hover:bg-brand-900'}`}
                    >
                      {n.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </aside>
        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2">
            <button className="rounded p-2 ring-1 ring-slate-300 lg:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Abrir menu">
              ☰
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <GlobalSearch />
              {can('analytics:read') && (
                <NavLink to="/assistente" className="hidden whitespace-nowrap rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 sm:block">
                  Assistente
                </NavLink>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button className="rounded px-2 py-1 text-sm ring-1 ring-slate-300" onClick={() => setShowNotes(!showNotes)} aria-expanded={showNotes}>
                  Notificações{unread ? ` (${unread})` : ''}
                </button>
                {showNotes && (
                  <div className="absolute right-0 mt-2 max-h-96 w-80 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                    {notes.data?.length ? (
                      notes.data.map((n) => (
                        <div key={n.id} className="border-b border-slate-100 p-2 text-sm last:border-0">
                          <div className="font-medium">{n.title}</div>
                          {n.body && <div className="whitespace-pre-line text-xs text-slate-600">{n.body}</div>}
                          <div className="text-[11px] text-slate-500">{fmtDate(n.createdAt)}</div>
                        </div>
                      ))
                    ) : (
                      <p className="p-2 text-sm text-slate-500">Sem notificações.</p>
                    )}
                  </div>
                )}
              </div>
              <span className="text-sm">
                <span className="font-medium">{me?.user.name}</span> <span className="text-slate-500">· {ROLE_LABEL[me?.user.role ?? ''] ?? me?.user.role}</span>
              </span>
              <button
                className="rounded px-2 py-1 text-sm text-brand-700 underline"
                onClick={async () => {
                  await logout();
                  nav('/login');
                }}
              >
                Sair
              </button>
            </div>
          </header>
          <main id="conteudo" className="mx-auto max-w-7xl p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
